import React, { useState, useRef } from 'react';
import { useDocumentTitle } from '../../../hooks/useDocumentTitle';
import { createPortal } from 'react-dom';
import '../../../styles/contacts.css';
import {
  Typography,
  Form,
  Input,
  Button,
  Checkbox,
  PhoneNumber,
  Select,
  Badge,
  DatePicker,
  Modal,
} from '@crisellevaldez/speculo-ui';
import { useToast } from '@crisellevaldez/speculo-ui';
import { useApolloClient } from '@apollo/client/react';
import { GET_CONTACT } from '../../../lib/graphql/queries/contacts';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { contactSchema, ContactFormData } from '../../../schemas/contact.schema';
import { Contact } from '../../../types/contact.types';
import { ContactActivityTabs } from './activity/ContactActivityTabs';
import { TagInput } from '../../common/TagInput';
import { buildTransferLineOptions } from '../../../utils/flows/formatTransferNumbers';
import { formatPhoneNumber } from '../../../utils/formatters';
import { CrmSourceIndicator } from './CrmSourceIndicator';
import { EAlertModal } from './EAlertModal';
import { getIntentStyle, getLeadSourceStyle } from '../../../utils/badgeStyles';
import {
  Mail,
  Globe,
  TrendingUp,
  Home,
  DollarSign,
  RotateCw,
  Info,
  CheckCircle2,
  XCircle,
  ChevronUp,
  ChevronDown,
  StickyNote,
  PhoneCall,
  BellRing,
  Phone,
  MapPin,
  Pencil,
  PauseCircle,
  PlayCircle,
  HelpCircle,
} from 'lucide-react';
import { SuggestedStatusCard } from './remi/SuggestedStatusCard';
import { unpauseContact, stripManualPausePrefix, formatPauseReason } from '../../../api/contacts';
import { CurrentStatusSummary } from './remi/CurrentStatusSummary';
import { SummarizeButton } from './remi/SummarizeButton';
import { CircularLeadScoreDisplay } from './CircularLeadScoreDisplay';
import { ConfirmationDialog } from '../../common/dialogs/ConfirmationDialog';
import { giveBackToRemi } from '../../../api/handoff';
import { useAuthStore } from '../../../contexts/auth/useAuthStore';
import { useUnsubscribeEAlert } from '../../../hooks/contacts/useEAlerts';
import { Tooltip } from '../../common/Tooltip';
import { useContactData } from '../../../hooks/contacts/useContactData';
import { getEAlertStatusSummary } from '../../../utils/ealertStatus';
import { formatOptInDate } from '../../../utils/dateTime';
import { resolveAssignedAgent, formatAssignedAgentName } from '../../../utils/assignedAgent';
import { startContactDispatch } from '../../../api/dispatch';

/* ── Collapsible section with horizontal border ─────────────── */
const Section: React.FC<{
  title: string;
  defaultOpen?: boolean;
  actions?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, defaultOpen = true, actions, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-foreground/10 py-5 md:py-6 xl:py-7 first:pt-0 last:border-b-0">
      <div className="flex items-center justify-between mb-2.5 xl:mb-3">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="flex items-center gap-1.5 group"
        >
          <span className="text-[10.5px] font-bold text-foreground/60 uppercase tracking-[0.1em]">
            {title}
          </span>
          {open ? (
            <ChevronUp className="t-icon-sm group-hover:text-foreground transition-colors" />
          ) : (
            <ChevronDown className="t-icon-sm group-hover:text-foreground transition-colors" />
          )}
        </button>
        {actions && open && (
          <div
            className="flex items-center gap-1.5 flex-wrap justify-end"
            onClick={(e) => e.stopPropagation()}
          >
            {actions}
          </div>
        )}
      </div>
      {open && <div>{children}</div>}
    </div>
  );
};

/* ── Per-channel consent pill (SPEC-1712) — commented out for now.
const CONSENT_STATE_STYLE: Record<string, string> = {
  OPTED_IN: 'bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300',
  OPTED_OUT: 'bg-muted text-muted-foreground',
  DNC: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300',
  UNKNOWN: 'bg-muted text-muted-foreground',
};

const ConsentPill: React.FC<{
  icon: React.ReactNode;
  label: string;
  state?: string;
  description?: string | null;
}> = ({ icon, label, state = 'UNKNOWN', description }) => {
  const style = CONSENT_STATE_STYLE[state] || CONSENT_STATE_STYLE.UNKNOWN;
  const pill = (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${style}`}
    >
      {icon}
      {label}
    </span>
  );
  if (!description) return pill;
  return (
    <Tooltip content={description} position="top">
      {pill}
    </Tooltip>
  );
};
*/

type EditingSection = 'contact' | 'details' | null;

interface ContactEditContentProps {
  contact: Contact | null | { id: string };
  onSubmit: (data: ContactFormData) => Promise<void>;
  onSuccess?: (data: ContactFormData) => void;
  defaultTab?:
    | 'activity'
    | 'notes'
    | 'emails'
    | 'website'
    | 'calls'
    | 'scheduled_calls'
    | 'saved_properties'
    | 'home_valuations'
    | 'saved_search';
  onEAlertClick?: () => void;
  containerClassName?: string;
  showHeader?: boolean;
  /** When set, the compact hero bar is rendered (via portal) into this element
   *  instead of inline. Lets the modal hoist the hero into Modal.Header. */
  heroTarget?: HTMLElement | null;
  dateRange?: { from: Date | null; to: Date | null };
}

export const ContactEditContent: React.FC<ContactEditContentProps> = ({
  contact,
  onSuccess,
  defaultTab = 'activity',
  onEAlertClick,
  containerClassName = '',
  showHeader = true,
  heroTarget,
  dateRange,
}) => {
  const [isEAlertModalOpen, setIsEAlertModalOpen] = useState(false);
  const [isUnsubscribeDialogOpen, setIsUnsubscribeDialogOpen] = useState(false);
  const [eAlertEditMode, setEAlertEditMode] = useState(false);
  const [isOptOutPending, setIsOptOutPending] = useState(false);
  const [isDispatchStarting, setIsDispatchStarting] = useState(false);
  // Non-Flywheel orgs have no SuggestedStatusCard, so the header surfaces the
  // paused state + Resume control instead.
  const [isHeaderResuming, setIsHeaderResuming] = useState(false);
  const [editingSection, setEditingSection] = useState<EditingSection>(null);
  const [infoModalOpen, setInfoModalOpen] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [tabKey, setTabKey] = useState(0);
  const { user: authUser } = useAuthStore();
  const { mutate: unsubscribeEAlert, isPending: isUnsubscribing } = useUnsubscribeEAlert();
  const { showToast } = useToast();
  // Apollo client — needed to evict the cached Contact entity after a
  // Give-back-to-Remi so the next read pulls fresh suggestedStatus /
  // claimedLead from the server instead of replaying the stale claimed
  // snapshot.
  const apolloClient = useApolloClient();

  const {
    register,
    handleSubmit,
    formState: { errors, dirtyFields },
    reset,
    setValue,
    watch,
    setError,
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      phoneNumber: '',
      phoneStatus: '',
      email: '',
      address: { street: '', city: '', state: '', zip: '' },
      opt_in: false,
      opt_in_date: '',
      opt_in_desc: '',
      call_tester: false,
      tags: [],
      claimedLead: undefined,
      assignedAgentId: null,
      assignedAgent: null,
    },
  });

  const {
    fullContact: localFullContact,
    setFullContact,
    isLoading,
    isSubmitting,
    sessionText,
    setSessionText,
    organization,
    users,
    loadingTeamMembers,
    canSetupEAlerts,
    contactId,
    fetchAndSetContact,
    handleClaimedLeadChange,
    handleFormSubmit,
    handleOptInChange,
    getSelectedTeamMember,
    handleTagsChange,
  } = useContactData({
    contact,
    reset,
    setValue,
    setError,
    onSuccess,
  });

  // Render-time fallback: if the local state hasn't been hydrated yet but
  // the prop already carries full contact data (the GQL-mapped Contact
  // passed by EditContactModal), use the prop directly. This avoids a
  // skeleton flash on the render immediately after the GQL load resolves —
  // the local state catches up one render later via fetchAndSetContact.
  const fullContact: Contact | null =
    localFullContact ??
    (contact && typeof contact === 'object' && 'firstName' in contact
      ? (contact as Contact)
      : null);

  // Sync contact data when the prop's id changes. Compare against the
  // *local* state so the sync still fires on first hydration / contact
  // switch — using the derived fullContact would skip the call and leave
  // optimistic-update handlers without a local copy to mutate.
  const localFullContactId = localFullContact ? localFullContact.id : null;
  if (contactId !== localFullContactId && !isLoading && contactId) {
    if (localFullContact) setFullContact(null);
    setSessionText('');
    fetchAndSetContact();
  }

  // Override the browser tab title with the best identifier we have for the
  // contact. Priority: full name → phone → email → "Edit Contact" (default
  // from PageLayout). Restores the previous title on unmount.
  const contactTitleLabel = (() => {
    if (!fullContact) return null;
    const name = `${fullContact.firstName ?? ''} ${fullContact.lastName ?? ''}`.trim();
    if (name) return name;
    if (fullContact.phoneNumber) return fullContact.phoneNumber;
    if (fullContact.email) return fullContact.email;
    return null;
  })();
  useDocumentTitle(contactTitleLabel);

  const assistantName = organization?.assistant_name || 'Remi';
  const flywheelEnabled = organization?.settings?.flywheel_enabled;
  const ealertStatus = getEAlertStatusSummary(fullContact ?? undefined);
  const remiDispatchEnabled = import.meta.env.VITE_REMI_DISPATCH_ENABLED === 'true';

  const getDispatchErrorMessage = (error: unknown): string => {
    const responseData = (error as { response?: { data?: unknown } })?.response?.data;
    if (responseData && typeof responseData === 'object' && 'detail' in responseData) {
      const detail = (responseData as { detail?: unknown }).detail;
      if (detail && typeof detail === 'object' && 'message' in detail) {
        return String((detail as { message?: unknown }).message || 'Unable to start Dispatch.');
      }
      if (typeof detail === 'string') return detail;
    }
    return 'Unable to start Dispatch.';
  };

  const handleCallAction = async (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!fullContact?.phoneNumber) {
      event.preventDefault();
      showToast({ message: 'This contact is not callable.', variant: 'error' });
      return;
    }

    if (!remiDispatchEnabled) {
      return;
    }

    event.preventDefault();
    if (isDispatchStarting) return;

    setIsDispatchStarting(true);
    showToast({ message: 'Starting Dispatch...', variant: 'info' });
    try {
      await startContactDispatch({
        contactId: fullContact.id,
        idempotencyKey: `contact-call:${fullContact.id}:${Date.now()}`,
      });
      showToast({ message: 'Remi is calling you now.', variant: 'success' });
    } catch (error) {
      showToast({ message: getDispatchErrorMessage(error), variant: 'error' });
    } finally {
      setIsDispatchStarting(false);
    }
  };

  /* ── Form helpers ────────────────────────────────────────── */
  const resetFormToContact = () => {
    if (fullContact) {
      reset({
        firstName: fullContact.firstName || '',
        lastName: fullContact.lastName || '',
        phoneNumber: fullContact.phoneNumber || '',
        phoneStatus: fullContact.phoneStatus || '',
        email: fullContact.email || '',
        address: {
          street: fullContact.address?.street || '',
          city: fullContact.address?.city || '',
          state: fullContact.address?.state || '',
          zip: fullContact.address?.zip || '',
        },
        opt_in: fullContact.opt_in || false,
        opt_in_date: (() => {
          const raw = fullContact.opt_in_date;
          if (!raw) return '';
          if (typeof raw === 'object' && 'seconds' in (raw as Record<string, unknown>)) {
            return new Date((raw as { seconds: number }).seconds * 1000)
              .toISOString()
              .split('T')[0];
          }
          const d = new Date(raw as string);
          return !isNaN(d.getTime()) ? d.toISOString().split('T')[0] : '';
        })(),
        opt_in_desc: fullContact.opt_in_desc || '',
        call_tester: fullContact.call_tester || false,
        tags: fullContact.tags || [],
        claimedLead: fullContact.claimedLead || undefined,
        assignedAgentId: fullContact.assignedAgentId ?? null,
        assignedAgent: fullContact.assignedAgent ?? null,
      });
    }
  };

  const handleSectionSave = () => {
    handleSubmit(async (data) => {
      await handleFormSubmit(data, dirtyFields as Partial<Record<keyof ContactFormData, unknown>>);
      setEditingSection(null);
    })();
  };

  const handleSectionCancel = () => {
    resetFormToContact();
    setEditingSection(null);
  };

  const activityTabsRef = useRef<HTMLDivElement>(null);

  const switchTab = (tab: string) => {
    setActiveTab(tab as typeof defaultTab);
    setTabKey((k) => k + 1);
    // Bring the right pane into view on narrow viewports where it sits
    // below the contact card — without this the user clicks the address
    // and visually nothing changes because the tab bar is off-screen.
    requestAnimationFrame(() => {
      activityTabsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  // When the user changes tabs from within the TabBar, bring the active
  // panel into view. Every tab — including SMS — aligns to the top of
  // the panel; the SmsTab handles its own latest-message affordance via
  // its internal "See latest messages" pill, so the modal no longer
  // walks scrollable ancestors and yanks the page to the bottom.
  const handleChildTabChange = (t: string) => {
    setActiveTab(t as typeof defaultTab);
    requestAnimationFrame(() => {
      activityTabsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  /* ── Format helpers ──────────────────────────────────────── */
  const formatAddress = () => {
    const a = fullContact?.address;
    if (!a || (!a.street && !a.city && !a.state && !a.zip)) return null;
    const line1 = a.street || '';
    const cityStateZip = [[a.city, a.state].filter(Boolean).join(', '), a.zip]
      .filter(Boolean)
      .join(' ');
    return { line1, line2: cityStateZip };
  };

  /* ── Header (page mode) ─────────────────────────────────── */
  const renderHeader = () => (
    <div>
      {isLoading ? (
        <div className="h-10 w-40 bg-muted animate-pulse rounded" />
      ) : (
        <>
          <div className="flex items-center gap-2">
            <Typography variant="h2">
              {fullContact && (fullContact.firstName || fullContact.lastName)
                ? `${fullContact.firstName || ''} ${fullContact.lastName || ''}`.trim()
                : 'Edit Contact'}
            </Typography>
            {fullContact && (
              <CrmSourceIndicator crmSynced={fullContact.crmSynced} crmID={fullContact.crmID} />
            )}
          </div>
          {!isLoading && sessionText && (
            <span className="t-caption text-green-600 font-medium ml-8">{sessionText}</span>
          )}
          {flywheelEnabled && fullContact?.flywheelOptOut === false && (
            <div className="flex items-center gap-1.5 sm:ml-8 mt-2">
              <span className="t-body text-amber-600 font-medium">
                Enrolled in Follow-up Flywheel
              </span>
              <Tooltip
                content={`${assistantName} analyzes activity and schedules follow-ups at optimal times.`}
                position="bottom"
              >
                <Info className="t-icon hover:text-amber-600 cursor-help" />
              </Tooltip>
            </div>
          )}
          {flywheelEnabled && fullContact?.claimedLead && fullContact?.flywheelOptOut === true && (
            <div className="flex items-center gap-1.5 sm:ml-8 mt-2 flex-wrap">
              <span className="t-body text-amber-600 font-medium">
                Claimed by: {fullContact.claimedLead.name}
              </span>
              <Tooltip
                content={`This lead has been claimed and removed from ${assistantName}'s automated follow-up system.`}
                position="bottom"
              >
                <Info className="t-icon hover:text-amber-600 cursor-help" />
              </Tooltip>
              <span className="t-label">·</span>
              <button
                type="button"
                onClick={handleGiveBackToRemiFromCard}
                className="t-body text-blue-600 font-medium underline hover:text-blue-700"
              >
                Give back to {assistantName}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );

  /* ── Inline: Contact info edit form ─────────────────────── */
  const renderContactEdit = () => (
    <div className="space-y-3 pb-2 border border-foreground/10 bg-muted/50 rounded-lg p-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Form.Control required>
          <Form.Label>First name</Form.Label>
          <Input
            placeholder="First name"
            {...register('firstName')}
            error={errors.firstName?.message}
          />
        </Form.Control>
        <Form.Control>
          <Form.Label>Last name</Form.Label>
          <Input
            placeholder="Last name"
            {...register('lastName')}
            error={errors.lastName?.message}
          />
        </Form.Control>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Form.Control>
          <Form.Label>Phone</Form.Label>
          <PhoneNumber
            value={watch('phoneNumber')}
            onChange={(value) => setValue('phoneNumber', value || '', { shouldDirty: true })}
            error={errors.phoneNumber?.message}
            defaultCountry="US"
            placeholder="Phone number"
            isLoading={isSubmitting}
          />
        </Form.Control>
        <Form.Control>
          <Form.Label>Email</Form.Label>
          <Input
            type="email"
            placeholder="Email"
            {...register('email')}
            error={errors.email?.message}
          />
        </Form.Control>
      </div>
      <Form.Control>
        <Form.Label>Street</Form.Label>
        <Input
          placeholder="Street address"
          {...register('address.street')}
          error={errors.address?.street?.message}
        />
      </Form.Control>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <Form.Control>
          <Form.Label>City</Form.Label>
          <Input
            placeholder="City"
            {...register('address.city')}
            error={errors.address?.city?.message}
          />
        </Form.Control>
        <Form.Control>
          <Form.Label>State</Form.Label>
          <Input
            placeholder="State"
            {...register('address.state')}
            error={errors.address?.state?.message}
          />
        </Form.Control>
        <Form.Control>
          <Form.Label>Zip</Form.Label>
          <Input
            placeholder="Zip"
            {...register('address.zip')}
            error={errors.address?.zip?.message}
          />
        </Form.Control>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <Button variant="outline" size="xs" onClick={handleSectionCancel} type="button">
          Cancel
        </Button>
        <Button
          type="button"
          size="xs"
          onClick={handleSectionSave}
          isLoading={isSubmitting}
          disabled={isSubmitting}
        >
          Save
        </Button>
      </div>
    </div>
  );

  /* ── Inline: Details edit form ──────────────────────────── */
  const renderDetailsEdit = () => (
    <div className="space-y-3 border border-foreground/10 bg-muted/50 rounded-lg p-3">
      <div className="flex items-center space-x-2">
        <Checkbox
          id="optedIn"
          checked={watch('opt_in')}
          onChange={async (e) => {
            const checked = (e.target as HTMLInputElement).checked;
            setValue('opt_in', checked, { shouldDirty: false });
            setIsOptOutPending(true);
            try {
              await handleOptInChange(checked);
            } catch (error) {
              setValue('opt_in', !checked, { shouldDirty: false });
              showToast({
                message:
                  error instanceof Error
                    ? error.message
                    : `Failed to ${checked ? 'opt in' : 'opt out'} contact`,
                variant: 'error',
              });
            } finally {
              setIsOptOutPending(false);
            }
          }}
          disabled={isOptOutPending}
        />
        <Form.Label htmlFor="optedIn" className="mb-0">
          Opted In
        </Form.Label>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Form.Control>
          <Form.Label>Opt-in Date</Form.Label>
          <DatePicker
            value={(() => {
              const v = watch('opt_in_date');
              if (!v) return null;
              const d = new Date(v);
              return !isNaN(d.getTime()) ? d : null;
            })()}
            onChange={(date) =>
              setValue('opt_in_date', date ? date.toISOString().split('T')[0] : '', {
                shouldValidate: true,
                shouldDirty: true,
              })
            }
            placeholder="Select date"
            error={errors.opt_in_date?.message}
          />
        </Form.Control>
        <Form.Control>
          <Form.Label>Opt In Desc</Form.Label>
          <Input {...register('opt_in_desc')} />
        </Form.Control>
      </div>
      <Form.Control>
        <Form.Label>Source</Form.Label>
        <Input {...register('leadSource')} />
      </Form.Control>
      <Form.Control>
        <Form.Label>Tags</Form.Label>
        <TagInput
          tags={watch('tags') || []}
          onTagsChange={handleTagsChange}
          placeholder="Type a tag and press Enter"
        />
      </Form.Control>
      <Form.Control>
        <div className="flex items-center gap-1.5">
          <Form.Label>Assigned To</Form.Label>
          <Tooltip content="This updates the owner in your connected CRM." position="top">
            <Info className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground cursor-help" />
          </Tooltip>
        </div>
        {fullContact?.activeHandoffId && (
          <div className="flex items-center gap-2 t-caption text-amber-600 mb-1">
            <Info className="t-icon-sm" />
            <span>Locked — active handoff</span>
          </div>
        )}
        <div className="flex items-center gap-2">
          <div className="flex-grow">
            <Select
              placeholder="Select team member"
              options={buildTransferLineOptions(users)}
              value={getSelectedTeamMember()}
              onChange={handleClaimedLeadChange}
              isLoading={loadingTeamMembers}
              disabled={!!fullContact?.activeHandoffId}
              searchable
            />
          </div>
          <Button
            variant="outline"
            className="p-1 h-8 w-[38px] flex items-center justify-center"
            onClick={() => handleClaimedLeadChange('')}
            type="button"
            title="Clear"
            disabled={!!fullContact?.activeHandoffId}
          >
            <RotateCw className="t-icon" />
          </Button>
        </div>
      </Form.Control>
      <div className="flex items-center space-x-2">
        <Checkbox
          id="testContact"
          checked={watch('call_tester')}
          onChange={(e) =>
            setValue('call_tester', (e.target as HTMLInputElement).checked, { shouldDirty: true })
          }
        />
        <Form.Label className="mb-0" htmlFor="testContact">
          Test Contact
        </Form.Label>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <Button variant="outline" size="xs" onClick={handleSectionCancel} type="button">
          Cancel
        </Button>
        <Button
          type="button"
          size="xs"
          onClick={handleSectionSave}
          isLoading={isSubmitting}
          disabled={isSubmitting}
        >
          Save
        </Button>
      </div>
    </div>
  );

  /* ────────────────────────────────────────────────────────── */
  /*  PROFILE VIEW                                              */
  /* ────────────────────────────────────────────────────────── */
  const renderProfileView = () => {
    if (!fullContact) return null;

    const addr = formatAddress();
    const formattedOptInDate = formatOptInDate(fullContact.opt_in_date);
    const tags = fullContact.tags || [];
    // Contact edit display follows authoritative assignment fields only.
    // `claimedLead` is legacy claim metadata and can be stale after pond sync.
    const resolvedAgent = resolveAssignedAgent(
      fullContact.assignedAgent,
      fullContact.assignedAgentId,
      users
    );
    const assignedAgentName =
      formatAssignedAgentName(resolvedAgent) ||
      (fullContact.assignedPondId ? 'Pond / Team Pool' : '');

    return (
      <div>
        {/* ═══ CONTACT INFO ════════════════════════════════════ */}
        <Section
          title="Contact Information"
          actions={
            editingSection !== 'contact' ? (
              <button
                type="button"
                onClick={() => setEditingSection('contact')}
                className="inline-flex items-center gap-1 text-[11px] font-medium text-foreground/70 hover:text-foreground underline underline-offset-2 decoration-foreground/30 hover:decoration-foreground transition-colors"
              >
                <Pencil className="w-3 h-3" />
                Edit
              </button>
            ) : undefined
          }
        >
          {editingSection === 'contact' ? (
            renderContactEdit()
          ) : (
            <div className="space-y-2">
              {/* Phone */}
              <div className="flex items-center gap-2.5 xl:gap-3">
                <div className="contact-icon-phone w-6 h-6 xl:w-8 xl:h-8 rounded-md flex items-center justify-center flex-shrink-0">
                  <Phone className="w-3 h-3 xl:w-4 xl:h-4" />
                </div>
                {fullContact.phoneNumber ? (
                  <a
                    href={`tel:${fullContact.phoneNumber}`}
                    className="no-link-style text-xs xl:text-sm text-foreground hover:text-primary transition-colors"
                  >
                    {formatPhoneNumber(fullContact.phoneNumber)}
                  </a>
                ) : (
                  <span className="text-xs xl:text-sm text-muted-foreground">No phone</span>
                )}
              </div>

              {/* Email */}
              <div className="flex items-center gap-2.5 xl:gap-3">
                <div className="contact-icon-email w-6 h-6 xl:w-8 xl:h-8 rounded-md flex items-center justify-center flex-shrink-0">
                  <Mail className="w-3 h-3 xl:w-4 xl:h-4" />
                </div>
                <div className="min-w-0 flex items-center gap-1.5">
                  {fullContact.email ? (
                    <>
                      <a
                        href={`mailto:${fullContact.email}`}
                        className="no-link-style text-xs xl:text-sm text-foreground hover:text-primary transition-colors truncate"
                      >
                        {fullContact.email}
                      </a>
                      {fullContact.email_status && (
                        <Badge
                          variant={
                            fullContact.email_status === 'active' ? 'success' : 'destructive'
                          }
                          className="px-1 py-0 leading-tight flex-shrink-0"
                        >
                          {fullContact.email_status}
                        </Badge>
                      )}
                    </>
                  ) : (
                    <span className="text-xs xl:text-sm text-muted-foreground">No email</span>
                  )}
                </div>
              </div>

              {/* Address — whole row (icon + street + city/state/zip) is
                  clickable and styled like the phone / email rows; click
                  switches the right pane to Homeowner data so the agent
                  can see property context. */}
              {addr ? (
                <button
                  type="button"
                  onClick={() => switchTab('property_insights')}
                  className="no-link-style flex items-start gap-2.5 xl:gap-3 text-left group"
                  title="Open Homeowner data"
                >
                  <div className="contact-icon-address w-6 h-6 xl:w-8 xl:h-8 rounded-md flex items-center justify-center flex-shrink-0 text-foreground group-hover:text-primary transition-colors">
                    <MapPin className="w-3 h-3 xl:w-4 xl:h-4" />
                  </div>
                  <div className="text-xs xl:text-sm text-foreground group-hover:text-primary transition-colors pt-0.5">
                    {addr.line1 && <div>{addr.line1}</div>}
                    {addr.line2 && <div className="text-foreground/70 group-hover:text-primary/70">{addr.line2}</div>}
                  </div>
                </button>
              ) : (
                <div className="flex items-start gap-2.5 xl:gap-3">
                  <div className="contact-icon-address w-6 h-6 xl:w-8 xl:h-8 rounded-md flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-3 h-3 xl:w-4 xl:h-4" />
                  </div>
                  <span className="text-xs xl:text-sm text-muted-foreground pt-0.5">
                    No address
                  </span>
                </div>
              )}
            </div>
          )}
        </Section>

        {/* ═══ QUICK ACTIONS ═══════════════════════════════════ */}
        <Section title="Quick Actions">
          <div className="grid grid-cols-2 gap-2 w-full">
            <a
              href={fullContact.phoneNumber ? `tel:${fullContact.phoneNumber}` : undefined}
              onClick={handleCallAction}
              className={`flex items-center gap-2 py-2.5 px-3 rounded-lg border bg-card action-card-green transition-all duration-150 ${
                fullContact.phoneNumber && !isDispatchStarting
                  ? 'hover:shadow-md cursor-pointer'
                  : 'opacity-40 cursor-not-allowed'
              }`}
              aria-disabled={!fullContact.phoneNumber || isDispatchStarting}
            >
              <div className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center action-icon-green">
                <PhoneCall className="w-3.5 h-3.5" />
              </div>
              <div className="min-w-0 leading-tight">
                <span className="text-xs xl:text-sm font-medium text-foreground block">
                  {isDispatchStarting ? 'Calling you now' : 'Call'}
                </span>
                <span className="text-[11px] text-muted-foreground hidden sm:block">
                  {remiDispatchEnabled ? 'Remi Dispatch' : 'Make a call'}
                </span>
              </div>
            </a>
            <a
              href={fullContact.email ? `mailto:${fullContact.email}` : undefined}
              className={`flex items-center gap-2 py-2.5 px-3 rounded-lg border bg-card action-card-blue transition-all duration-150 ${
                fullContact.email
                  ? 'hover:shadow-md cursor-pointer'
                  : 'opacity-40 cursor-not-allowed'
              }`}
            >
              <div className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center action-icon-blue">
                <Mail className="w-4 h-4" />
              </div>
              <div className="min-w-0 leading-tight">
                <span className="text-xs xl:text-sm font-medium text-foreground block">Email</span>
                <span className="text-[11px] text-muted-foreground hidden sm:block">
                  Send email
                </span>
              </div>
            </a>
            <button
              type="button"
              onClick={() => switchTab('notes')}
              className="flex items-center gap-2 py-2.5 px-3 rounded-lg border bg-card action-card-yellow hover:shadow-md transition-all duration-150 text-left"
            >
              <div className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center action-icon-yellow">
                <StickyNote className="w-3.5 h-3.5" />
              </div>
              <div className="min-w-0 leading-tight">
                <span className="text-xs xl:text-sm font-medium text-foreground block">Note</span>
                <span className="text-[11px] text-muted-foreground hidden sm:block">
                  Write a note
                </span>
              </div>
            </button>
            {canSetupEAlerts && (() => {
              // eAlerts are email-delivered, so disable the setup tile
              // when the contact has no email on file. Existing
              // registrations remain editable so the agent can fix /
              // unsubscribe even if the email field is empty.
              const hasEmail = Boolean(fullContact.email);
              const isRegistered = !!fullContact.has_ealerts_registered;
              const disabled = !hasEmail && !isRegistered;
              return (
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => {
                    if (disabled) return;
                    if (fullContact.has_ealerts_registered) {
                      setEAlertEditMode(true);
                    } else {
                      setEAlertEditMode(false);
                      if (onEAlertClick) onEAlertClick();
                    }
                    setIsEAlertModalOpen(true);
                  }}
                  title={disabled ? 'Add an email to set up an eAlert' : undefined}
                  className={`flex items-center gap-2 py-2.5 px-3 rounded-lg border bg-card action-card-purple transition-all duration-150 text-left ${
                    disabled ? 'opacity-40 cursor-not-allowed' : 'hover:shadow-md'
                  }`}
                >
                  <div className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center action-icon-purple">
                    <BellRing className="w-3.5 h-3.5" />
                  </div>
                  <div className="min-w-0 leading-tight">
                    {/* Title row matches the Note tile's "Note" line —
                        flex wrapper now carries the body text size so
                        the line-box doesn't fall back to the browser
                        default (~1.5×) and add a gap above "Setup". */}
                    <span className="flex items-center gap-1.5 text-xs xl:text-sm leading-tight">
                      <span className="font-medium text-foreground">eAlert</span>
                      {ealertStatus.isRegistered && (
                        <span className="inline-flex items-center text-[9px] font-semibold uppercase tracking-wide px-1.5 py-1 rounded-full bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300 leading-none">
                          Active
                        </span>
                      )}
                    </span>
                    <span className="block text-[11px] text-muted-foreground leading-tight">
                      {disabled
                        ? 'No email on file'
                        : ealertStatus.isRegistered
                          ? 'Edit registration'
                          : 'Setup'}
                      {ealertStatus.isRegistered && !disabled && (
                        <>
                          {' · '}
                          <span
                            role="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setIsUnsubscribeDialogOpen(true);
                            }}
                            className="text-red-500 dark:text-red-400 hover:underline"
                          >
                            Unsub
                          </span>
                        </>
                      )}
                    </span>
                  </div>
                </button>
              );
            })()}
          </div>
        </Section>

        {/* ═══ LEAD INFO ═════════════════════════════════════════ */}
        <Section
          title="Lead Info"
          actions={
            editingSection !== 'details' ? (
              <button
                type="button"
                onClick={() => setEditingSection('details')}
                className="inline-flex items-center gap-1 text-[11px] font-medium text-foreground/70 hover:text-foreground underline underline-offset-2 decoration-foreground/30 hover:decoration-foreground transition-colors"
              >
                <Pencil className="w-3 h-3" />
                Edit
              </button>
            ) : undefined
          }
        >
          {editingSection === 'details' ? (
            renderDetailsEdit()
          ) : (
            <div className="space-y-0 info-divider">
              {/* Opt-in */}
              <div className="flex items-center justify-between py-2 xl:py-3 first:pt-0">
                <span className="text-xs xl:text-sm text-muted-foreground flex-shrink-0">
                  Opt-in
                </span>
                <span className="text-xs xl:text-sm text-foreground text-right flex items-center gap-1.5">
                  {fullContact.opt_in ? (
                    <CheckCircle2 className="w-3.5 h-3.5 check-green" />
                  ) : (
                    <XCircle className="w-3.5 h-3.5 text-muted-foreground" />
                  )}
                  {formattedOptInDate && (
                    <span className="text-xs xl:text-sm text-foreground">
                      {fullContact.opt_in
                        ? formattedOptInDate
                        : `Last opted in ${formattedOptInDate}`}
                    </span>
                  )}
                </span>
              </div>

              {/* Opt In Desc */}
              <div className="flex items-center justify-between py-2 xl:py-3">
                <span className="text-xs xl:text-sm text-muted-foreground flex-shrink-0">
                  Opt In Desc
                </span>
                <div className="text-right min-w-0">
                  {fullContact.opt_in_desc ? (
                    /^https?:\/\//i.test(fullContact.opt_in_desc) ? (
                      <a
                        href={fullContact.opt_in_desc}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs xl:text-sm text-primary underline truncate block"
                        title={fullContact.opt_in_desc}
                      >
                        {fullContact.opt_in_desc.replace(/^https?:\/\/(www\.)?/i, '').split('/')[0]}
                      </a>
                    ) : (
                      <span className="text-xs xl:text-sm text-foreground truncate block">
                        {fullContact.opt_in_desc}
                      </span>
                    )
                  ) : (
                    <span className="text-xs xl:text-sm text-muted-foreground">—</span>
                  )}
                </div>
              </div>

              {/* Channel consent — SPEC-1712. Commented out for now.
              {fullContact.consent && (
                <div className="flex items-center justify-between gap-4 py-2 xl:py-3">
                  <span className="text-xs xl:text-sm text-muted-foreground flex-shrink-0 flex items-center gap-1">
                    Channel consent
                    <Tooltip
                      content="Per-channel opt-in state derived from the contact's opt-in, email, and phone status."
                      position="right"
                    >
                      <Info className="w-3 h-3 text-muted-foreground hover:text-foreground cursor-help" />
                    </Tooltip>
                  </span>
                  <div className="flex flex-wrap gap-1.5 justify-end">
                    <ConsentPill
                      icon={<MessageSquare className="w-3 h-3" />}
                      label="SMS"
                      state={fullContact.consent.sms?.state}
                      description={fullContact.consent.sms?.description}
                    />
                    <ConsentPill
                      icon={<Mail className="w-3 h-3" />}
                      label="Email"
                      state={fullContact.consent.email?.state}
                      description={fullContact.consent.email?.description}
                    />
                    <ConsentPill
                      icon={<Phone className="w-3 h-3" />}
                      label="Call"
                      state={fullContact.consent.call?.state}
                      description={fullContact.consent.call?.description}
                    />
                  </div>
                </div>
              )}
              */}

              {/* Source */}
              <div className="flex items-center justify-between py-2 xl:py-3">
                <span className="text-xs xl:text-sm text-muted-foreground flex-shrink-0 flex items-center gap-1">
                  Source
                  <Tooltip
                    content="Where this contact originated — e.g., CRM sync, manual entry, database upload, or website form."
                    position="right"
                  >
                    <Info className="w-3 h-3 text-muted-foreground hover:text-foreground cursor-help" />
                  </Tooltip>
                </span>
                <div className="text-right min-w-0">
                  {fullContact.leadSource ? (
                    <span className="text-xs xl:text-sm text-foreground truncate block">
                      {fullContact.leadSource}
                    </span>
                  ) : (
                    <span className="text-xs xl:text-sm text-muted-foreground">—</span>
                  )}
                </div>
              </div>

              {/* Intent & Lead Source */}
              <div className="flex items-center justify-between py-2 xl:py-3">
                <span className="text-xs xl:text-sm text-muted-foreground flex-shrink-0 flex items-center gap-1">
                  Intent / Lead Source
                  <Tooltip
                    content="Determined from Speculo campaigns. Intent is Buyer or Seller based on their campaign activity. Lead Source: PPC = paid ads, Organic = leads from IDX, Email = leads from eAlerts."
                    position="bottom"
                  >
                    <Info className="w-3 h-3 text-muted-foreground hover:text-foreground cursor-help" />
                  </Tooltip>
                </span>
                <div className="flex items-center gap-1.5">
                  {fullContact.leadIntent ? (
                    <Badge
                      className={`${getIntentStyle(fullContact.leadIntent).background} ${getIntentStyle(fullContact.leadIntent).text} gap-1`}
                    >
                      {fullContact.leadIntent === 'Buyer' && <DollarSign className="w-3 h-3" />}
                      {fullContact.leadIntent === 'Seller' && <Home className="w-3 h-3" />}
                      {fullContact.leadIntent}
                    </Badge>
                  ) : (
                    <span className="text-xs xl:text-sm text-muted-foreground">—</span>
                  )}
                  {fullContact.leadSourceSecondary ? (
                    <Badge
                      className={`${getLeadSourceStyle(fullContact.leadSourceSecondary).background} ${getLeadSourceStyle(fullContact.leadSourceSecondary).text} gap-1`}
                    >
                      {fullContact.leadSourceSecondary === 'Email' && <Mail className="w-3 h-3" />}
                      {fullContact.leadSourceSecondary === 'PPC' && (
                        <TrendingUp className="w-3 h-3" />
                      )}
                      {fullContact.leadSourceSecondary === 'Organic' && (
                        <Globe className="w-3 h-3" />
                      )}
                      {fullContact.leadSourceSecondary}
                    </Badge>
                  ) : (
                    <span className="text-xs xl:text-sm text-muted-foreground">—</span>
                  )}
                </div>
              </div>

              {/* Assigned To (CRM-synced owner) — universal */}
              <div className="flex items-center justify-between py-2 xl:py-3">
                <span className="text-xs xl:text-sm text-muted-foreground flex-shrink-0 flex items-center gap-1">
                  Assigned To
                  <Tooltip
                    content="CRM-synced owner of this lead. Editable from the contact edit form; updates push to your connected CRM."
                    position="bottom"
                  >
                    <Info className="w-3 h-3 text-muted-foreground hover:text-foreground cursor-help" />
                  </Tooltip>
                </span>
                <span
                  className={`text-xs xl:text-sm ${assignedAgentName ? 'text-foreground' : 'text-muted-foreground'}`}
                >
                  {assignedAgentName || 'Unassigned'}
                </span>
              </div>

              {/* Tags */}
              <div className="flex items-center justify-between gap-4 py-2 xl:py-3">
                <span className="text-xs xl:text-sm text-muted-foreground flex-shrink-0">Tags</span>
                {tags.length > 0 ? (
                  <div className="flex flex-wrap gap-x-1 gap-y-1 justify-end">
                    {tags.map((tag, i) => (
                      <Badge key={i} className="badge-amber">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <span className="text-xs xl:text-sm text-muted-foreground">No tags</span>
                )}
              </div>

              {/* Test Contact */}
              <div className="flex items-center justify-between py-2 xl:py-3 last:pb-0">
                <div>
                  <span className="text-xs xl:text-sm text-muted-foreground">Test Contact</span>
                  <p className="text-xs xl:text-sm text-muted-foreground leading-tight mt-0.5">
                    Used to test call flows on the Flows page
                  </p>
                </div>
                {fullContact.call_tester ? (
                  <CheckCircle2 className="w-4 h-4 check-green flex-shrink-0" />
                ) : (
                  <XCircle className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                )}
              </div>
            </div>
          )}
        </Section>

        {/* ═══ ENGAGEMENT (commented out) ═════════════════════ */}
        {/* <Section title="Engagement">
          <div className="space-y-0 info-divider">
            <div className="flex items-center justify-between py-2 xl:py-3.5 first:pt-0">
              <span className="text-xs xl:text-sm text-muted-foreground flex-shrink-0">Last Call</span>
              <span className="text-sm text-foreground capitalize">
                {fullContact.recentOutcome
                  ? fullContact.recentOutcome.replace(/_/g, ' ')
                  : '—'}
              </span>
            </div>
            <div className="flex items-center justify-between py-2 xl:py-3.5">
              <span className="text-xs xl:text-sm text-muted-foreground flex-shrink-0">Last Email</span>
              <span className="text-sm text-muted-foreground">—</span>
            </div>
            <div className="flex items-center justify-between py-2 xl:py-3.5">
              <span className="text-xs xl:text-sm text-muted-foreground flex-shrink-0">Last Website Visit</span>
              <span className="text-sm text-muted-foreground">—</span>
            </div>
            <div className="flex items-center justify-between py-2 xl:py-3.5">
              <span className="text-xs xl:text-sm text-muted-foreground flex-shrink-0">Lead Score</span>
              <span className="text-sm text-foreground font-medium">
                {typeof fullContact.score === 'number' ? `${fullContact.score}%` : '—'}
              </span>
            </div>
            <div className="flex items-center justify-between py-2 xl:py-3.5 last:pb-0">
              <span className="text-xs xl:text-sm text-muted-foreground flex-shrink-0">Last Note</span>
              <span className="text-sm text-muted-foreground">—</span>
            </div>
          </div>
        </Section> */}
      </div>
    );
  };

  const handlePausedFromCard = (rawReason: string) => {
    if (!fullContact) return;
    // Optimistic update so the card flips to the picked Paused state without
    // waiting for the GraphQL refetch the backend's contact.updated event
    // will eventually trigger. The display layer strips the `manual:` prefix
    // when rendering. We deliberately don't call fetchAndSetContact() here —
    // with a full prop it re-applies the stale (not-yet-paused) prop and
    // clobbers this update; server truth syncs in via contact.updated.
    setFullContact({
      ...fullContact,
      aiPaused: true,
      aiPausedAt: new Date().toISOString(),
      aiPausedReason: `manual: ${rawReason}`,
    });
  };

  // Mirror of handlePausedFromCard for resume — the card already called the
  // unpause endpoint, so just clear the paused state optimistically. We do NOT
  // call fetchAndSetContact() here: when the prop already carries full data it
  // re-applies the stale (still-paused) prop and clobbers this update. Server
  // truth syncs in later via the backend's contact.updated event.
  const handleResumedFromCard = () => {
    if (!fullContact) return;
    setFullContact({
      ...fullContact,
      aiPaused: false,
      aiPausedAt: undefined,
      aiPausedReason: undefined,
    });
  };

  // Resume from the header control (non-Flywheel orgs). Calls the unpause
  // endpoint, then reuses the optimistic clear above.
  const handleResumeFromHeader = async () => {
    if (isHeaderResuming || !fullContact?.id || !organization?.id) return;
    setIsHeaderResuming(true);
    const contactName =
      `${fullContact.firstName ?? ''} ${fullContact.lastName ?? ''}`.trim() || 'this contact';
    try {
      await unpauseContact(fullContact.id, organization.id);
      showToast({
        message: `${assistantName} has resumed outreach for ${contactName}.`,
        variant: 'success',
      });
      handleResumedFromCard();
    } catch (e) {
      showToast({
        message: e instanceof Error ? e.message : 'Failed to resume. Please try again.',
        variant: 'error',
      });
    } finally {
      setIsHeaderResuming(false);
    }
  };

  const handleGiveBackToRemiFromCard = async () => {
    if (
      !fullContact?.id ||
      !fullContact.activeHandoffId ||
      !organization?.id ||
      !authUser?.uid
    )
      return;
    try {
      // See useContactData for why no agentId is sent.
      const result = await giveBackToRemi({
        organizationId: organization.id,
        contactId: fullContact.id,
        handoffId: fullContact.activeHandoffId,
      });
      if (result.success) {
        showToast({
          message: `Lead returned to ${assistantName}. Automated follow-ups resumed.`,
          variant: 'success',
        });
        // Re-run every live GET_CONTACT observer so the card reads the
        // real post-give-back state (suggestedStatus, claimedLead,
        // flywheel membership) from the server. Same pattern editContact
        // uses after a save — see `refetchContacts` in
        // useContactMutations. The `onQueryUpdated` guard skips zombie
        // observers mounted with an empty contactId.
        await apolloClient.refetchQueries({
          include: [GET_CONTACT],
          onQueryUpdated(observableQuery) {
            const vars = observableQuery.options.variables as
              | { contactId?: string }
              | undefined;
            return !!vars?.contactId;
          },
        });
        setFullContact(null);
      } else {
        showToast({
          // The backend returns a raw "agentId must match authenticated user"
          // 403 when the lead is assigned to a different agent — show the agent
          // what to do instead of the technical detail.
          message: /must match authenticated user/i.test(result.error ?? '')
            ? `You can only give back a lead that's assigned to you — ask the assigned agent or an admin to release it.`
            : result.error || `Failed to return lead to ${assistantName}`,
          variant: 'error',
        });
      }
    } catch (error) {
      console.error('Error opting into flywheel:', error);
      showToast({
        message: `Failed to return lead to ${assistantName}. Please try again.`,
        variant: 'error',
      });
    }
  };

  /* ── Compact contact-info bar (always visible at top) ─── */
  const renderCompactBar = () => {
    if (!fullContact) return null;

    const fullName =
      `${fullContact.firstName || ''} ${fullContact.lastName || ''}`.trim() || 'Unnamed contact';

    return (
      <div className="px-1 py-1">
        {/* Slim hero row — full contact info lives in the left rail of the
            two-column body now, so the hero only needs identity + sync
            badges + a Remi-paused indicator. */}
        {/* Mobile: score + CRM badge sit on the top row, then name + paused
            below. md+ collapses back to side-by-side. */}
        <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 min-w-0">
          {/* Mobile top row — score + CRM badge inline. */}
          <div className="flex items-center gap-3 md:gap-0 shrink-0">
            <CircularLeadScoreDisplay
              contact={fullContact}
              size="md"
              onScoreCalculated={setFullContact}
            />
            <div className="md:hidden flex items-center gap-1.5">
              <CrmSourceIndicator crmSynced={fullContact.crmSynced} crmID={fullContact.crmID} />
              <SummarizeButton contact={fullContact} />
            </div>
          </div>

          {/* Name + sync badges on the first row; the Remi-paused indicator
              sits on its own row BELOW the name. */}
          <div className="flex-1 min-w-0 flex flex-col gap-1.5">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
              <h2 className="text-base lg:text-lg xl:text-xl font-bold text-foreground leading-tight truncate">
                {fullName}
              </h2>
              {/* Sync badge — only inline next to the name at md+ (already
                  rendered up top on mobile). */}
              <span className="hidden md:inline-flex">
                <CrmSourceIndicator crmSynced={fullContact.crmSynced} crmID={fullContact.crmID} />
              </span>
              <span className="hidden md:inline-flex">
                <SummarizeButton contact={fullContact} />
              </span>
            </div>

            {/* Non-Flywheel orgs have no SuggestedStatusCard. Surface the
                paused state + Resume below the name (old position). No
                manual-pause entry point for non-Flywheel — resume only. */}
            {!flywheelEnabled && fullContact.aiPaused && (
              <div className="flex items-center flex-wrap gap-x-2 gap-y-1">
                <span className="inline-flex items-center gap-1 text-[11px] sm:text-xs font-medium text-foreground/70">
                  <PauseCircle className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-foreground/60 flex-shrink-0" />
                  {assistantName} is paused
                </span>
                {fullContact.aiPausedReason && (
                  <Tooltip
                    content={
                      /^manual:\s*/i.test(fullContact.aiPausedReason)
                        ? `Manual: ${stripManualPausePrefix(fullContact.aiPausedReason)}`
                        : formatPauseReason(fullContact.aiPausedReason)
                    }
                    position="bottom"
                  >
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground cursor-help">
                      <HelpCircle className="h-3.5 w-3.5" />
                      Why?
                    </span>
                  </Tooltip>
                )}
                <button
                  type="button"
                  onClick={handleResumeFromHeader}
                  disabled={isHeaderResuming}
                  className="inline-flex items-center gap-1 text-[11px] sm:text-xs font-medium px-2 py-0.5 rounded border border-amber-300/70 bg-amber-50 text-amber-700 hover:bg-amber-100 hover:border-amber-400 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300 dark:hover:bg-amber-500/20 transition-colors disabled:opacity-60 disabled:cursor-default"
                >
                  <PlayCircle className="h-3 w-3" strokeWidth={2.25} />
                  {isHeaderResuming ? 'Resuming…' : `Resume ${assistantName}`}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  /* ── Skeleton for compact bar + tabs ────────────────────── */
  const renderSkeleton = () => (
    <div className="space-y-3 animate-pulse">
      <div className="rounded-xl border border-foreground/20 bg-card px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="h-4 w-32 bg-muted rounded" />
          <div className="h-4 w-48 bg-muted rounded" />
          <div className="h-5 w-16 bg-muted rounded-full" />
          <div className="ml-auto h-7 w-24 bg-muted rounded" />
          <div className="h-7 w-16 bg-muted rounded" />
        </div>
      </div>
      <div className="rounded-xl border border-foreground/20 bg-card px-4 py-4">
        <div className="h-4 w-40 bg-muted rounded mb-3" />
        <div className="h-3 w-3/4 bg-muted rounded mb-2" />
        <div className="h-3 w-1/2 bg-muted rounded" />
      </div>
      <div className="flex gap-2 border-b border-foreground/10 pb-2">
        {[80, 60, 56, 70, 96, 80, 96, 96].map((w, i) => (
          <div key={i} className="h-7 rounded bg-muted" style={{ width: w }} />
        ))}
      </div>
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="flex items-start gap-3 p-3 rounded-lg border border-foreground/10">
          <div className="w-8 h-8 rounded-full bg-muted flex-shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-32 bg-muted rounded" />
            <div className="h-3 w-3/4 bg-muted rounded" />
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className={`flex flex-col gap-3 flex-1 min-h-0 ${containerClassName || ''}`}>
      {showHeader && <div className="bg-background z-10 flex-shrink-0">{renderHeader()}</div>}

      {/* Loading skeleton — covers both regions */}
      {isLoading || !fullContact ? (
        <div className="flex-grow min-h-0 overflow-y-auto scrollbar-custom contact-vertical-body">
          {renderSkeleton()}
        </div>
      ) : (
        <>
          {/* Hero (contact header) — pinned at the top on lg+ so it stays
              visible while the content below scrolls. On mobile it lives
              in the normal flow and scrolls with the page. When heroTarget
              is provided (modal mode), portal the hero into that node so
              the modal's body has full vertical space for activity tabs. */}
          {heroTarget ? (
            createPortal(renderCompactBar(), heroTarget)
          ) : (
            <div className="flex-shrink-0 contact-vertical-body">
              {renderCompactBar()}
            </div>
          )}

          {/* Scrollable region — Suggested Status + tabs + side panel.
              On mobile: scrolls with the page. On lg+: flex column.
              overflow-y-auto so when the SuggestedStatusCard + min-height
              activity row don't fit the viewport, the region scrolls
              instead of crushing the activity tabs to a few rows. */}
          {/* Two-column body — left rail keeps the full contact info
              (Contact Info, Lead Info, Property Insights, etc.) always
              visible; right rail stacks the Remi cards + activity tabs. */}
          {/* Two-column body. Page (outer) scrolls naturally — each column
              expands to its natural content height. min-height keeps the
              shorter column from collapsing too far on tall content. */}
          <div className="flex-grow min-h-0 overflow-y-auto scrollbar-custom contact-vertical-body py-2 pr-2 lg:flex lg:flex-col">
            {/* Grid alignment + right-column layout depend on TWO flags:

                - `isFlywheel`: SuggestedStatusCard renders on every tab.
                - `isCommTab` : we're on the "Communication so far" tab
                  where Future outreach + activity feed both render.

                Only when BOTH are true do we apply the tall floor layout
                (items-start + min-h). Every other combination uses the
                absolute-inset-0 trick so the right column follows the
                LEFT rail's natural height. */}
            <div
              className="grid grid-cols-1 lg:grid-cols-[minmax(0,440px)_minmax(0,1fr)] xl:grid-cols-[minmax(0,500px)_minmax(0,1fr)] 2xl:grid-cols-[minmax(0,640px)_minmax(0,1fr)] 3xl:grid-cols-[minmax(0,760px)_minmax(0,1fr)] gap-4 lg:gap-6 lg:flex-1 lg:grid-rows-[minmax(0,1fr)]"
            >
              {/* LEFT — Current status summary on top, then the full
                  contact info rail (Contact Info, Lead Info, Property
                  Insights, etc.). */}
              <div className="flex flex-col gap-4 lg:gap-5 lg:min-h-[600px] lg:h-full">
                <CurrentStatusSummary contact={(contact as Contact) ?? fullContact} />
                <aside className="contact-left-col rounded-xl border border-foreground/10 shadow-md p-3.5 md:p-4 flex-1">
                  {renderProfileView()}
                </aside>
              </div>

              {/* RIGHT — Suggested Status + activity tabs.

                  On lg+: `lg:relative` + the inner `lg:absolute lg:inset-0`
                  empties the right column of in-flow content, so its
                  intrinsic size is 0 and the grid row sizes to the LEFT
                  column. Default `align-items: stretch` then stretches the
                  right cell to match left; the absolute-positioned inner
                  fills that cell, and the activity-tabs panel scrolls
                  internally if its content exceeds the left's height.
                  Mobile/sm uses the normal flow (single column). */}
              {/* RIGHT — two different layout strategies depending on
                  flywheel state:

                  - Flywheel ON: in-flow children with `lg:min-h-[1180px]`
                    on the wrapper. The grid uses `items-start` so this
                    min-h applies ONLY to the right column — LEFT stays
                    natural. The activity panel gets ~720px even though
                    SuggestedStatus + FutureOutreach + section header
                    eat ~620px above it.

                  - Flywheel OFF: empty-in-flow wrapper + absolute inset-0
                    inner. Right column's intrinsic size is 0, so grid row
                    sizes to the LEFT column. align-items: stretch (default
                    in this branch) pulls right to match — activity panel
                    follows left. */}
              {(() => {
                const isFlywheel = flywheelEnabled && fullContact?.flywheelOptOut === false;
                const isCommTab = activeTab === 'activity';
                // Show the card on flywheel orgs only when there's something
                // for it to pick — AI-paused or an AI suggestion of HANDOFF /
                // NURTURING. Claimed leads always carry HANDOFF, so the
                // HANDOFF condition covers them.
                const showSuggestedStatus =
                  Boolean(flywheelEnabled) &&
                  Boolean(
                    fullContact?.aiPaused === true ||
                      fullContact?.suggestedStatus?.status === 'HANDOFF' ||
                      fullContact?.suggestedStatus?.status === 'NURTURING',
                  );
                // The flywheel-comm-tab branch uses an in-flow flex
                // column with a FIXED-height activity card — this is the
                // only layout where the plan and the feed both need
                // explicit room. Without a plan, fall through to the
                // shared (other-tabs) branch so the activity card uses
                // the absolute-inset-0 trick and tracks LEFT exactly,
                // matching Emails / Calls / etc.
                const hasFollowUpPlan =
                  (fullContact.followUpPlan?.steps?.length ?? 0) > 0;
                if (isFlywheel && isCommTab && hasFollowUpPlan) {
                  return (
                    <div className="flex flex-col gap-4 lg:gap-5 min-w-0">
                      {showSuggestedStatus && (
                        <SuggestedStatusCard
                          contact={fullContact ?? (contact as Contact)}
                          assistantName={assistantName}
                          onGiveBackToRemi={handleGiveBackToRemiFromCard}
                          onPaused={handlePausedFromCard}
                          onResumed={handleResumedFromCard}
                        />
                      )}
                      {/* Fixed height — the plan + feed both need explicit
                          room. Without this cap the plan would crush the
                          feed, or stretching to LEFT would overflow the
                          modal. 480px floor for mobile. */}
                      {contactId && (
                        <div ref={activityTabsRef} className="flex flex-col min-w-0 min-h-[480px] lg:h-[900px]">
                          <ContactActivityTabs
                            key={tabKey}
                            contactId={contactId}
                            firebaseUid={fullContact?.firebaseUid}
                            contactFirstName={fullContact?.firstName}
                            contact={(contact as Contact) ?? fullContact}
                            defaultTab={activeTab}
                            initialDateRange={dateRange}
                            onContactUpdate={fetchAndSetContact}
                            onTabChange={handleChildTabChange}
                          />
                        </div>
                      )}
                    </div>
                  );
                }
                return (
                  <div className="min-w-0 lg:relative">
                    <div className="flex flex-col gap-4 lg:gap-5 lg:absolute lg:inset-0">
                      {showSuggestedStatus && (
                        <SuggestedStatusCard
                          contact={fullContact ?? (contact as Contact)}
                          assistantName={assistantName}
                          onGiveBackToRemi={handleGiveBackToRemiFromCard}
                          onPaused={handlePausedFromCard}
                          onResumed={handleResumedFromCard}
                        />
                      )}
                      {contactId && (
                        <div ref={activityTabsRef} className="flex flex-col min-w-0 min-h-[480px] lg:min-h-0 flex-1">
                          <ContactActivityTabs
                            key={tabKey}
                            contactId={contactId}
                            firebaseUid={fullContact?.firebaseUid}
                            contactFirstName={fullContact?.firstName}
                            contact={(contact as Contact) ?? fullContact}
                            defaultTab={activeTab}
                            initialDateRange={dateRange}
                            onContactUpdate={fetchAndSetContact}
                            onTabChange={handleChildTabChange}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </>
      )}

      {/* "View all info" / "Edit" modal — surfaces the legacy profile view
          (Contact Info, Quick Actions, Lead Info, Property Insights) for
          read + inline-edit, replacing the old left-column rail. */}
      <Modal
        isOpen={infoModalOpen}
        onClose={() => {
          setInfoModalOpen(false);
          setEditingSection(null);
        }}
        size="lg"
      >
        <Modal.Header>
          <Typography variant="h3" className="font-semibold">
            Contact information
          </Typography>
        </Modal.Header>
        <Modal.Body>{fullContact ? renderProfileView() : null}</Modal.Body>
        <Modal.Footer>
          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={() => {
                setInfoModalOpen(false);
                setEditingSection(null);
              }}
            >
              Close
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <EAlertModal
        isOpen={isEAlertModalOpen}
        onClose={() => {
          setIsEAlertModalOpen(false);
          setEAlertEditMode(false);
        }}
        contact={contact}
        isEditMode={eAlertEditMode}
      />

      <ConfirmationDialog
        isOpen={isUnsubscribeDialogOpen}
        onClose={() => setIsUnsubscribeDialogOpen(false)}
        onConfirm={() => {
          if (contactId) {
            unsubscribeEAlert(
              { contactId, organizationId: organization?.id || '' },
              {
                onSuccess: () => {
                  showToast({
                    message: 'Successfully unsubscribed from eAlert',
                    variant: 'success',
                  });
                  if (fullContact)
                    setFullContact({ ...fullContact, has_ealerts_registered: false });
                  setIsUnsubscribeDialogOpen(false);
                },
                onError: (error) => {
                  showToast({
                    message:
                      error instanceof Error ? error.message : 'Failed to unsubscribe from eAlert',
                    variant: 'error',
                  });
                },
              }
            );
          }
        }}
        title="Unsubscribe from eAlert"
        message={`Are you sure you want to unsubscribe ${
          fullContact
            ? fullContact.firstName || fullContact.lastName || 'this contact'
            : 'this contact'
        } from eAlert?`}
        confirmLabel="Unsubscribe"
        cancelLabel="Cancel"
        variant="danger"
        isLoading={isUnsubscribing}
      />
    </div>
  );
};
